import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { MapPin, Plus, Trash2, Search, Download, Upload } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';

const AddressesTab = () => {
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newAddress, setNewAddress] = useState('');
  const [search, setSearch] = useState('');
  const [editingIdx, setEditingIdx] = useState(null);
  const [editValue, setEditValue] = useState('');

  const loadAddresses = useCallback(async () => {
    try {
      const resp = await axios.get(`${API}/admin/addresses`);
      setAddresses(resp.data.addresses || []);
    } catch {
      toast.error('Erreur lors du chargement des adresses');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAddresses(); }, [loadAddresses]);

  const addAddress = async (e) => {
    e.preventDefault();
    if (!newAddress.trim()) return;
    try {
      await axios.post(`${API}/admin/addresses`, { address: newAddress.trim() });
      setNewAddress('');
      toast.success('Adresse ajoutée');
      loadAddresses();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur');
    }
  };

  const deleteAddress = async (address) => {
    try {
      await axios.delete(`${API}/admin/addresses`, { data: { address } });
      toast.success('Adresse supprimée');
      loadAddresses();
    } catch {
      toast.error('Erreur lors de la suppression');
    }
  };

  const saveEdit = async (oldAddress) => {
    const trimmed = editValue.trim();
    if (!trimmed || trimmed === oldAddress) {
      setEditingIdx(null);
      return;
    }
    try {
      const updated = addresses.map(a => a === oldAddress ? trimmed : a);
      await axios.put(`${API}/admin/addresses`, { addresses: updated });
      toast.success('Adresse modifiée');
      setEditingIdx(null);
      loadAddresses();
    } catch {
      toast.error('Erreur lors de la modification');
    }
  };

  const exportCSV = () => {
    const csv = addresses.join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'adresses_blainville.csv';
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Export CSV téléchargé');
  };

  const importCSV = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (ev) => {
      const text = ev.target.result;
      const lines = text.split('\n').map(l => l.trim()).filter(Boolean);
      if (lines.length === 0) { toast.error('Fichier vide'); return; }
      try {
        const merged = [...new Set([...addresses, ...lines])];
        await axios.put(`${API}/admin/addresses`, { addresses: merged });
        toast.success(`${lines.length} adresses importées`);
        loadAddresses();
      } catch {
        toast.error("Erreur lors de l'import");
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const filtered = search
    ? addresses.filter(a => a.toLowerCase().includes(search.toLowerCase()))
    : addresses;

  if (loading) return <div className="text-center py-8 text-gray-500">Chargement...</div>;

  return (
    <div data-testid="admin-addresses-tab" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold" style={{ color: '#002768' }}>Adresses de Blainville</h2>
          <p className="text-gray-500 text-sm mt-1">
            Ces adresses sont utilisées par le simulateur RAO et les scénarios pour générer des variantes réalistes.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold px-3 py-1 rounded-full" style={{ backgroundColor: '#EFF6FF', color: '#1E40AF' }}>
            {addresses.length} adresses
          </span>
        </div>
      </div>

      {/* Add + Search + Import/Export */}
      <Card className="glass border-0">
        <CardContent className="pt-4 space-y-3">
          <form onSubmit={addAddress} className="flex gap-2">
            <div className="flex-1 relative">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={newAddress}
                onChange={e => setNewAddress(e.target.value)}
                placeholder="Ajouter une adresse (ex: 123 rue Exemple, Blainville)"
                className="w-full pl-10 pr-4 py-2.5 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 text-sm"
                style={{ '--tw-ring-color': '#2E6CF5' }}
                data-testid="add-address-input"
              />
            </div>
            <button
              type="submit"
              disabled={!newAddress.trim()}
              className="px-4 py-2.5 text-white rounded-lg font-medium disabled:opacity-50 transition-all flex items-center gap-1.5"
              style={{ backgroundColor: '#24B219' }}
              data-testid="add-address-btn"
            >
              <Plus className="w-4 h-4" />
              <span className="hidden sm:inline">Ajouter</span>
            </button>
          </form>

          <div className="flex gap-2 items-center">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Rechercher une adresse..."
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-1 text-sm"
                data-testid="search-address-input"
              />
            </div>
            <button onClick={exportCSV} className="px-3 py-2 text-sm rounded-lg border border-gray-200 hover:bg-gray-50 flex items-center gap-1.5 text-gray-600" data-testid="export-csv-btn">
              <Download className="w-4 h-4" />
              <span className="hidden sm:inline">CSV</span>
            </button>
            <label className="px-3 py-2 text-sm rounded-lg border border-gray-200 hover:bg-gray-50 flex items-center gap-1.5 text-gray-600 cursor-pointer" data-testid="import-csv-btn">
              <Upload className="w-4 h-4" />
              <span className="hidden sm:inline">Importer</span>
              <input type="file" accept=".csv,.txt" onChange={importCSV} className="hidden" />
            </label>
          </div>
        </CardContent>
      </Card>

      {/* Address List */}
      <Card className="glass border-0">
        <CardHeader className="py-3 px-4">
          <CardTitle className="text-base flex items-center gap-2">
            <MapPin className="w-4 h-4" style={{ color: '#002768' }} />
            <span style={{ color: '#002768' }}>
              {search ? `${filtered.length} résultat${filtered.length > 1 ? 's' : ''}` : 'Toutes les adresses'}
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0 px-4 pb-4">
          <div className="max-h-[500px] overflow-y-auto space-y-1" data-testid="address-list">
            {filtered.length === 0 ? (
              <p className="text-center text-gray-400 py-6 text-sm">
                {search ? 'Aucun résultat' : 'Aucune adresse enregistrée'}
              </p>
            ) : filtered.map((address, idx) => (
              <div
                key={`${address}-${idx}`}
                data-testid={`address-item-${idx}`}
                className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-gray-50 group transition-colors"
              >
                {editingIdx === idx ? (
                  <input
                    autoFocus
                    value={editValue}
                    onChange={e => setEditValue(e.target.value)}
                    onBlur={() => saveEdit(address)}
                    onKeyDown={e => { if (e.key === 'Enter') saveEdit(address); if (e.key === 'Escape') setEditingIdx(null); }}
                    className="flex-1 px-2 py-1 border rounded text-sm mr-2 focus:outline-none focus:ring-1"
                    data-testid="edit-address-input"
                  />
                ) : (
                  <span
                    className="text-sm text-gray-700 flex-1 cursor-pointer hover:text-blue-700"
                    onClick={() => { setEditingIdx(idx); setEditValue(address); }}
                    title="Cliquer pour modifier"
                  >
                    {address}
                  </span>
                )}
                <button
                  onClick={() => deleteAddress(address)}
                  className="opacity-0 group-hover:opacity-100 p-1.5 rounded-lg text-red-400 hover:text-red-600 hover:bg-red-50 transition-all"
                  data-testid={`delete-address-${idx}`}
                  title="Supprimer"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AddressesTab;
